import * as React from 'react';
import { AdfoxContextProvider, AdfoxContextData } from '../../context/AdfoxContext';
import { PParams } from '../../../types';
import { AdfoxBannerContainer } from '../../../AdfoxBannerContainer';
import { useBooleanState } from '@cian/react-utils';
import { Experiment } from '@cian/experiments/ab';
import {
  NEWBUILDING_BUZZOLA_INTEGRATION_TEST_WEB,
  useNewbuildingBuzzoolaFirstEnabled,
  useNewbuildingBuzzoolaIntegrationAsStubEnabled,
  useNewbuildingBuzzoolaIntegrationTestWebAllowedRegion,
  useNewbuildingBuzzoolaIntegrationTestWebExpEnabled,
} from '../../../../../hooks/useNewbuildingBuzzoolaIntegrationTestWebAvailable';
import { BuzzoolaBannerContainer } from '../../../../BuzzoolaBanner';

/**
 * @todo Заменить типы
 * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
 * Если переходим на базуллу, то заменить на типы базуллы.
 * Если остаёмся на адфоксе, то оставить как есть.
 */
const POST_VIEW_ANALYTICS_DATA: AdfoxContextData['postViewAnalyticsData'] = {
  place: 'between_offers',
  place__DEPRECATED: 'native_tgb',
  name__DEPRECATED: 'Listing_native_TGB',
  extra__DEPRECATED: {
    offerType: 'TGB_banner',
  },
};
/**
 * @todo Удалить переменную
 * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
 * Если переходим на базуллу, то удалить.
 * Если остаёмся на адфоксе, то оставить как есть.
 */
const P_PARAMS: PParams = { p1: 'cusvh', p2: 'fkhj' };
/**
 * @todo Заменить типы
 * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
 * Если переходим на базуллу, то заменить на типы базуллы.
 * Если остаёмся на адфоксе, то оставить как есть.
 */
const SIZE: AdfoxContextData['size'] = { width: '100%', height: 276 };
/**
 * @todo Удалить переменную
 * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
 * Если переходим на базуллу, то оставить как есть.
 * Если остаёмся на адфоксе, то удалить.
 */
const PLACEMENT_ID = 1298544;

type TAdfoxBannerTGBBetweenOffersProps = {
  position: number;
};

/**
 * @todo Удалить компонент
 * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
 */
const TGBBannerExp: React.FC<TAdfoxBannerTGBBetweenOffersProps> = ({ position }) => {
  const isRegionAllowed = useNewbuildingBuzzoolaIntegrationTestWebAllowedRegion();

  if (!isRegionAllowed) {
    return (
      <AdfoxContextProvider postViewAnalyticsData={POST_VIEW_ANALYTICS_DATA} size={SIZE} pParams={P_PARAMS}>
        <AdfoxBannerContainer position={position} />
      </AdfoxContextProvider>
    );
  }

  return (
    <>
      <Experiment name={NEWBUILDING_BUZZOLA_INTEGRATION_TEST_WEB} group="Adfox" default>
        <AdfoxContextProvider postViewAnalyticsData={POST_VIEW_ANALYTICS_DATA} size={SIZE} pParams={P_PARAMS}>
          <AdfoxBannerContainer position={position} />
        </AdfoxContextProvider>
      </Experiment>
      <Experiment name={NEWBUILDING_BUZZOLA_INTEGRATION_TEST_WEB} group="Buzzoola">
        <BuzzoolaBannerContainer
          hideOnError
          hideOnNoad
          position={position}
          placementId={PLACEMENT_ID}
          sizes={SIZE}
          analyticsData={POST_VIEW_ANALYTICS_DATA}
        />
      </Experiment>
    </>
  );
};

/**
 * @todo Удалить компонент
 * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
 */
const TGBBannerStub: React.FC<TAdfoxBannerTGBBetweenOffersProps> = ({ position }) => {
  const { state, setTrue } = useBooleanState(false);

  if (state) {
    return (
      <BuzzoolaBannerContainer
        hideOnError
        hideOnNoad
        placementId={PLACEMENT_ID}
        sizes={SIZE}
        position={position}
        analyticsData={POST_VIEW_ANALYTICS_DATA}
      />
    );
  }

  return (
    <AdfoxContextProvider postViewAnalyticsData={POST_VIEW_ANALYTICS_DATA} size={SIZE} pParams={P_PARAMS}>
      <AdfoxBannerContainer position={position} onStub={setTrue} />
    </AdfoxContextProvider>
  );
};

export const AdfoxBannerTGBBetweenOffers = React.memo<TAdfoxBannerTGBBetweenOffersProps>(
  function AdfoxBannerTGBBetweenOffers(props) {
    const { position } = props;

    const isBuzzoolaExpEnabled = useNewbuildingBuzzoolaIntegrationTestWebExpEnabled();
    const isBuzzoolaAsStubEnabled = useNewbuildingBuzzoolaIntegrationAsStubEnabled();
    const isBuzzoolaFirstEnabled = useNewbuildingBuzzoolaFirstEnabled();

    /**
     * @todo Удалить ветвление
     * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
     * Если переходим на базуллу, то выносим компонент базуллы BuzzoolaBannerWithHide сюда,
     * ветвление в любом случае удаляется, компонента TGBBannerExp не должно быть.
     */
    if (isBuzzoolaExpEnabled) {
      return <TGBBannerExp {...props} />;
    }

    /**
     * @todo Удалить ветвление
     * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
     * Тут логику ветвления полностью убираем, так как тут просто включение базулы на постоянной основе, а результатом будет эксперимент,
     * это для того, что бы можно было включить баннеры для тестирования и подобных кейсов
     */
    if (isBuzzoolaFirstEnabled) {
      return (
        <BuzzoolaBannerContainer
          hideOnError
          hideOnNoad
          placementId={PLACEMENT_ID}
          sizes={SIZE}
          position={position}
          analyticsData={POST_VIEW_ANALYTICS_DATA}
        />
      );
    }

    /**
     * @todo Удалить ветвление
     * @description Данный функционал появился в задаче CD-243378, будет удалён в задаче CD-245113
     * Тут удаляем в любом случае, так как это промежуточная логика пилота, этой логики по планам не будет в стабильном коде.
     * Будет или только адфокс или базулла.
     */
    if (isBuzzoolaAsStubEnabled) {
      return <TGBBannerStub {...props} />;
    }

    return (
      <AdfoxContextProvider postViewAnalyticsData={POST_VIEW_ANALYTICS_DATA} size={SIZE} pParams={P_PARAMS}>
        <AdfoxBannerContainer position={position} />
      </AdfoxContextProvider>
    );
  },
);
